import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateAiUsage } from './useAiUsage';
import {
  identifyLivestockPhoto,
  deleteTempPhoto,
  type IdentifyLivestockPhotoParams,
  type LivestockIdentificationResponse,
} from '../services/livestockIdentificationService';

/** Same shape the service takes: the aquarium and the photo to identify. */
export type IdentifyPhotoParams = IdentifyLivestockPhotoParams;

export interface UseLivestockIdentificationResult {
  /** Trigger the identify-photo mutation (fire and forget). */
  submitPhoto: (params: IdentifyPhotoParams) => void;
  /** Trigger the identify-photo mutation and await its result. */
  submitPhotoAsync: (params: IdentifyPhotoParams) => Promise<LivestockIdentificationResponse>;
  /** True while the identify-photo request is in flight. */
  isIdentifying: boolean;
  /** Error from the most recent identify-photo attempt, if any. */
  identifyError: Error | null;
  /** Result of the most recent successful identify-photo attempt, if any. */
  identifyResult: LivestockIdentificationResponse | undefined;
  /** Trigger the delete-temp-photo mutation (fire and forget). */
  deleteTempPhoto: (params: { aquariumId: string; key: string }) => void;
  /** Trigger the delete-temp-photo mutation and await its completion. */
  deleteTempPhotoAsync: (params: { aquariumId: string; key: string }) => Promise<void>;
  /** True while the delete-temp-photo request is in flight. */
  isDeletingTempPhoto: boolean;
  /** Error from the most recent delete-temp-photo attempt, if any. */
  deleteError: Error | null;
  /** Clears both mutations' state (data, error, status). */
  reset: () => void;
}

/**
 * Wraps the livestock identification service in React Query mutations.
 *
 * Identification results are explicitly ephemeral (no DB row is created until the
 * user accepts and saves a livestock record elsewhere), so this hook only
 * refreshes the AI credit meter (an identification is an AI call).
 */
export function useLivestockIdentification(): UseLivestockIdentificationResult {
  const queryClient = useQueryClient();
  const identifyMutation = useMutation<LivestockIdentificationResponse, Error, IdentifyPhotoParams>(
    {
      mutationFn: (params: IdentifyPhotoParams) => identifyLivestockPhoto(params),
      onSettled: () => {
        invalidateAiUsage(queryClient);
      },
    },
  );

  const deleteMutation = useMutation<void, Error, { aquariumId: string; key: string }>({
    mutationFn: ({ aquariumId, key }: { aquariumId: string; key: string }) =>
      deleteTempPhoto(aquariumId, key),
  });

  const reset = (): void => {
    identifyMutation.reset();
    deleteMutation.reset();
  };

  return {
    submitPhoto: (params: IdentifyPhotoParams): void => {
      identifyMutation.mutate(params);
    },
    submitPhotoAsync: (params: IdentifyPhotoParams): Promise<LivestockIdentificationResponse> =>
      identifyMutation.mutateAsync(params),
    isIdentifying: identifyMutation.isPending,
    identifyError: identifyMutation.error,
    identifyResult: identifyMutation.data,
    deleteTempPhoto: (params: { aquariumId: string; key: string }): void => {
      deleteMutation.mutate(params);
    },
    deleteTempPhotoAsync: (params: { aquariumId: string; key: string }): Promise<void> =>
      deleteMutation.mutateAsync(params),
    isDeletingTempPhoto: deleteMutation.isPending,
    deleteError: deleteMutation.error,
    reset,
  };
}
